import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Workflow, User, CheckCircle2, Circle } from 'lucide-react';

const ClientWorkflowTab = ({ 
  clientPipelineAssignments, 
  pipelines 
}) => {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Workflow className="h-5 w-5" />
          Pipeline Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        {clientPipelineAssignments.length > 0 ? (
          <div className="space-y-6">
            {clientPipelineAssignments.map((assignment) => {
              const pipeline = pipelines.find(p => p._id === assignment.pipelineId);
              if (!pipeline) return null;
              
              const currentStage = pipeline.stages.find(s => s.id === assignment.stageId);
              const currentStageIndex = pipeline.stages.findIndex(s => s.id === assignment.stageId);
              const stageTasks = currentStage?.tasks || [];
              const completedTasks = assignment.completedStageTasks?.[assignment.stageId] || [];
              
              return (
                <div key={assignment._id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">{pipeline.name}</h3>
                      <p className="text-sm text-gray-500">Code: {pipeline.code}</p>
                    </div>
                    {assignment.assignedTo && (
                      <Badge variant="outline" className="gap-1">
                        <User className="h-3 w-3" />
                        {assignment.assignedTo}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Stage Progress Bar */}
                  <div className="flex items-center gap-1 mb-4">
                    {pipeline.stages.map((stage, index) => (
                      <div 
                        key={stage.id}
                        className={`flex-1 h-2 rounded-full transition-all ${
                          index < currentStageIndex 
                            ? 'bg-green-500' 
                            : index === currentStageIndex 
                              ? 'bg-blue-500' 
                              : 'bg-gray-200'
                        }`}
                        title={stage.name}
                      />
                    ))}
                  </div>
                  
                  {/* Stage Labels */}
                  <div className="flex items-center justify-between text-xs mb-4">
                    {pipeline.stages.map((stage, index) => (
                      <div 
                        key={stage.id}
                        className={`flex-1 text-center ${
                          index === currentStageIndex ? 'font-semibold text-blue-600' : 'text-gray-500'
                        }`}
                      >
                        {stage.name}
                      </div>
                    ))}
                  </div>
                  
                  {/* Current Stage Details */}
                  <div className="bg-blue-50 rounded-lg p-4 mt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: currentStage?.color }}
                      />
                      <h4 className="font-medium">Current Stage: {currentStage?.name}</h4>
                    </div>
                    
                    {stageTasks.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600 mb-2">
                          Tasks: {completedTasks.length}/{stageTasks.length} completed
                        </p>
                        {stageTasks.map((task) => {
                          const isCompleted = completedTasks.includes(task.id);
                          return (
                            <div key={task.id} className="flex items-start gap-2 text-sm">
                              {isCompleted ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                              ) : (
                                <Circle className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
                              )}
                              <span className={isCompleted ? 'text-gray-400 line-through' : 'text-gray-700'}>
                                {task.name}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No tasks for this stage</p>
                    )}
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/pipelines')}
                    >
                      View in Pipeline
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">Client is not assigned to any pipeline</p>
            <Button 
              variant="outline"
              onClick={() => navigate('/pipelines')}
            >
              Go to Pipelines
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientWorkflowTab;
